import { z } from "zod";

export const emailSchema = z.string().email().max(254).toLowerCase().trim();

export const requestOtpSchema = z.object({
  email: emailSchema,
  intent: z.enum(["creator", "business"]).default("creator"),
});
export type RequestOtpInput = z.infer<typeof requestOtpSchema>;

// 6-digit numeric one-time code paired with the email it was sent to.
export const verifyOtpSchema = z.object({
  email: emailSchema,
  code: z.string().regex(/^\d{6}$/, "must be 6 digits"),
});
export type VerifyOtpInput = z.infer<typeof verifyOtpSchema>;

export const userRoleSchema = z.enum(["creator", "business", "admin"]);
export type UserRole = z.infer<typeof userRoleSchema>;

export const userSchema = z.object({
  id: z.string(),
  email: emailSchema,
  role: userRoleSchema,
  displayName: z.string().nullable(),
  verified: z.boolean(),
  verificationRequestedAt: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  bio: z.string().nullable(),
  city: z.string().nullable(),
  neighborhood: z.string().nullable(),
  socialLinks: z.lazy(() => socialLinksSchema).nullable(),
  links: z.lazy(() => linksSchema).nullable(),
  createdAt: z.string(),
});
export type User = z.infer<typeof userSchema>;

// Computed verification state. Derived in the UI from User fields.
export type VerificationState = "unverified" | "pending" | "verified";
export function verificationStateOf(u: Pick<User, "verified" | "verificationRequestedAt">): VerificationState {
  if (u.verified) return "verified";
  if (u.verificationRequestedAt) return "pending";
  return "unverified";
}

// Creator (and any user) updates to their own profile.
export const updateProfileSchema = z.object({
  displayName: z.string().min(1).max(80).nullable().optional(),
  avatarUrl: z
    .union([z.string().url().max(500), z.literal("")])
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  bio: z.string().max(500).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  neighborhood: z.string().max(120).nullable().optional(),
  socialLinks: z.lazy(() => socialLinksSchema).nullable().optional(),
  links: z.lazy(() => linksSchema).nullable().optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export const perkSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  title: z.string().min(1).max(120),
  description: z.string().max(2000),
  active: z.boolean(),
  imageUrl: z.string().nullable(),
  // Only present in business-owner views and in the creator's view AFTER
  // the claim is approved. /search/nearby and public listings strip this.
  redemptionCode: z.string().nullable(),
  // Default false → claims are auto-approved on creation. Flip true to
  // make the business owner manually review each claim before the
  // creator gets the redemption code.
  requireApproval: z.boolean(),
  createdAt: z.string(),
});
export type Perk = z.infer<typeof perkSchema>;

export const businessPlanSchema = z.enum(["free", "pro"]);
export type BusinessPlan = z.infer<typeof businessPlanSchema>;

// Hours: one entry per day-of-week. `closed: true` means closed that day.
// Open ranges are 24h "HH:MM" strings. Single range per day for v1.
export const dayHoursSchema = z.union([
  z.object({ closed: z.literal(true) }),
  z.object({
    closed: z.literal(false),
    open: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "must be HH:MM"),
    close: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "must be HH:MM"),
  }),
]);
export type DayHours = z.infer<typeof dayHoursSchema>;

export const dayKeys = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type DayKey = (typeof dayKeys)[number];

export const businessHoursSchema = z.object({
  mon: dayHoursSchema,
  tue: dayHoursSchema,
  wed: dayHoursSchema,
  thu: dayHoursSchema,
  fri: dayHoursSchema,
  sat: dayHoursSchema,
  sun: dayHoursSchema,
});
export type BusinessHours = z.infer<typeof businessHoursSchema>;

export const socialPlatforms = [
  "instagram",
  "tiktok",
  "facebook",
  "youtube",
  "twitter",
] as const;
export type SocialPlatform = (typeof socialPlatforms)[number];

export const socialLinksSchema = z.object({
  instagram: z.string().max(120).optional(),
  tiktok: z.string().max(120).optional(),
  facebook: z.string().max(200).optional(),
  youtube: z.string().max(200).optional(),
  twitter: z.string().max(120).optional(),
});
export type SocialLinks = z.infer<typeof socialLinksSchema>;

// Free-form links beyond the 5 first-class socials — personal site,
// Substack, Patreon, Linktree, anywhere else a creator's work lives.
export const linkSchema = z.object({
  label: z.string().min(1).max(40),
  url: z.string().url().max(500),
});
export type Link = z.infer<typeof linkSchema>;
export const linksSchema = z.array(linkSchema).max(20);
export type Links = z.infer<typeof linksSchema>;

export const businessSchema = z.object({
  id: z.string(),
  ownerUserId: z.string(),
  name: z.string().min(1).max(120),
  slug: z.string().min(1).max(80),
  shortCode: z.string().min(6).max(12).nullable(),
  city: z.string().nullable(),
  address: z.string().nullable(),
  description: z.string().nullable(),
  plan: businessPlanSchema,
  websiteUrl: z.string().nullable(),
  logoUrl: z.string().nullable(),
  phone: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactName: z.string().nullable(),
  timezone: z.string().nullable(),
  hours: businessHoursSchema.nullable(),
  socialLinks: socialLinksSchema.nullable(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  geocodedAt: z.string().nullable(),
  geocodeSource: z.enum(["address", "city"]).nullable(),
  createdAt: z.string(),
});
export type Business = z.infer<typeof businessSchema>;

// ----- Nearby search + favorites -----

export const favoriteTargetTypeSchema = z.enum(["business", "perk"]);
export type FavoriteTargetType = z.infer<typeof favoriteTargetTypeSchema>;

export const favoriteSchema = z.object({
  targetType: favoriteTargetTypeSchema,
  targetId: z.string(),
});
export type Favorite = z.infer<typeof favoriteSchema>;

export type NearbyPerk = {
  id: string;
  title: string;
  description: string;
  active: boolean;
  imageUrl: string | null;
  requireApproval: boolean;
  // Intentionally no redemptionCode here — codes are only revealed to a
  // creator after their claim has been approved.
};

export type NearbyBusiness = {
  id: string;
  name: string;
  slug: string;
  shortCode: string | null;
  city: string | null;
  neighborhood: string | null;
  address: string | null;
  description: string | null;
  plan: BusinessPlan;
  logoUrl: string | null;
  websiteUrl: string | null;
  lat: number;
  lon: number;
  distanceKm: number;
  perks: NearbyPerk[];
  briefPublished: boolean;
};

export const createBusinessSchema = z.object({
  name: z.string().min(1).max(120),
  city: z.string().max(120).optional(),
  address: z.string().max(240).optional(),
  description: z.string().max(2000).optional(),
});
export type CreateBusinessInput = z.infer<typeof createBusinessSchema>;

// Partial update — every field optional, but if present must validate.
// `null` clears a field; `undefined`/missing leaves it alone.
const optStr = (max: number) => z.string().max(max).nullable().optional();
const optUrl = (max: number) =>
  z
    .union([z.string().url().max(max), z.literal("")])
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v));

export const updateBusinessSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  city: optStr(120),
  address: optStr(240),
  description: optStr(2000),
  websiteUrl: optUrl(500),
  logoUrl: optUrl(500),
  phone: optStr(40),
  contactEmail: z
    .union([emailSchema, z.literal("")])
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  contactName: optStr(120),
  timezone: optStr(60),
  hours: businessHoursSchema.nullable().optional(),
  socialLinks: socialLinksSchema.nullable().optional(),
});
export type UpdateBusinessInput = z.infer<typeof updateBusinessSchema>;

// Empty-string → null transforms so the client can blank a field by
// clearing the input. URLs are validated; codes are free-form short strings.
const optUrlOrNull = z
  .union([z.string().url().max(500), z.literal("")])
  .nullable()
  .optional()
  .transform((v) => (v === "" ? null : v));
const optCodeOrNull = z
  .union([z.string().max(40), z.literal("")])
  .nullable()
  .optional()
  .transform((v) => (v === "" ? null : v));

export const createPerkSchema = z.object({
  title: z.string().min(1).max(120),
  description: z.string().max(2000).default(""),
  imageUrl: optUrlOrNull,
  redemptionCode: optCodeOrNull,
  requireApproval: z.boolean().optional(),
});
export type CreatePerkInput = z.infer<typeof createPerkSchema>;

export const updatePerkSchema = z.object({
  title: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).optional(),
  active: z.boolean().optional(),
  imageUrl: optUrlOrNull,
  redemptionCode: optCodeOrNull,
  requireApproval: z.boolean().optional(),
});
export type UpdatePerkInput = z.infer<typeof updatePerkSchema>;

export const claimStatusSchema = z.enum(["pending", "approved", "fulfilled", "rejected"]);
export type ClaimStatus = z.infer<typeof claimStatusSchema>;

export const claimSchema = z.object({
  id: z.string(),
  perkId: z.string(),
  creatorUserId: z.string(),
  status: claimStatusSchema,
  contentUrl: z.string().nullable(),
  contentLinks: z.lazy(() => linksSchema).nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type Claim = z.infer<typeof claimSchema>;

export const updateClaimSchema = z.object({
  status: claimStatusSchema.optional(),
  // Legacy single URL — kept so existing flows don't break.
  contentUrl: z.string().url().max(500).optional(),
  // Preferred: one or more labelled links the creator wants to share with
  // the business. A non-empty list marks the claim fulfilled.
  contentLinks: z.lazy(() => linksSchema).nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
});
export type UpdateClaimInput = z.infer<typeof updateClaimSchema>;

// Public listing — strip redemptionCode (server enforces too).
export const perkWithBusinessSchema = perkSchema
  .omit({ redemptionCode: true })
  .extend({
    businessName: z.string(),
    businessCity: z.string().nullable(),
    briefPublished: z.boolean(),
  });
export type PerkWithBusiness = z.infer<typeof perkWithBusinessSchema>;

// ----- Creator briefs -----

export const briefStatusSchema = z.enum(["draft", "published"]);
export type BriefStatus = z.infer<typeof briefStatusSchema>;

// The 5 wizard questions
export const briefAnswersSchema = z.object({
  vibe: z.string().min(1).max(1000),
  highlight: z.string().min(1).max(1000),
  contentStyle: z.string().min(1).max(1000),
  noGos: z.string().min(1).max(1000),
  cta: z.string().min(1).max(1000),
});
export type BriefAnswers = z.infer<typeof briefAnswersSchema>;

export const briefSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  // Mirrored from businesses.short_code so the share URL can use the
  // short code without a second round-trip.
  businessShortCode: z.string().nullable(),
  status: briefStatusSchema,
  vibe: z.string(),
  highlight: z.string(),
  contentStyle: z.string(),
  noGos: z.string(),
  cta: z.string(),
  content: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string().nullable(),
});
export type Brief = z.infer<typeof briefSchema>;

export const updateBriefSchema = z.object({
  content: z.string().max(20000).optional(),
  status: briefStatusSchema.optional(),
  vibe: z.string().max(1000).optional(),
  highlight: z.string().max(1000).optional(),
  contentStyle: z.string().max(1000).optional(),
  noGos: z.string().max(1000).optional(),
  cta: z.string().max(1000).optional(),
});
export type UpdateBriefInput = z.infer<typeof updateBriefSchema>;

export const briefQuestions = [
  {
    key: "vibe" as const,
    label: "What's the vibe & personality of your business?",
    placeholder: "Cozy, third-wave, neighborhood gathering spot…",
  },
  {
    key: "highlight" as const,
    label: "What's the one thing you want creators to highlight?",
    placeholder: "Our weekend pour-over flight + the patio in the afternoon sun.",
  },
  {
    key: "contentStyle" as const,
    label: "What content style fits your brand?",
    placeholder: "Slow, aesthetic Reels with ambient audio — no fast-cut comedy.",
  },
  {
    key: "noGos" as const,
    label: "What's off-limits?",
    placeholder: "Don't film other customers. Don't compare us to chain brands.",
  },
  {
    key: "cta" as const,
    label: "Ideal call-to-action for creators?",
    placeholder: "Tag @coralroasters, drop a 'come hang' style caption, location-tag SF.",
  },
];

export function generateBriefTemplate(businessName: string, answers: BriefAnswers): string {
  return `# ${businessName} — Creator Brief

## About us
${answers.vibe}

## What we'd love you to focus on
${answers.highlight}

## Content style
${answers.contentStyle}

## Off-limits
${answers.noGos}

## Call-to-action
${answers.cta}

---

## Anything else
_Add specifics here — hashtags, tagging conventions, brand colors, examples of work you love, time-of-day guidance, etc._
`;
}
